import { motion } from 'framer-motion';
import { FileText, TrendingUp, BookOpen, Users, Shield, Building, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: FileText,
    title: "Individual Tax Preparation",
    description: "Expert preparation for every filing status — single, married filing jointly, married filing separately, and head of household. We handle complex situations including investments, rental income, stock options, multi-state filing, and major life changes like marriage, divorce, or home purchase.",
    link: "/services/tax"
  },
  {
    icon: TrendingUp,
    title: "Tax Planning & Strategy",
    description: "Year-round proactive planning for individuals and businesses to minimize your tax burden before filing season. Filing status optimization, retirement contributions, capital gains timing, education credits, and quarterly estimated tax management.",
    link: "/services/consulting"
  },
  {
    icon: BookOpen,
    title: "Business Tax & Accounting",
    description: "Complete business returns, entity structuring (LLC, S-Corp, C-Corp), clean monthly bookkeeping, QuickBooks setup, financial statements, and the day-to-day accounting so you can focus on running your business.",
    link: "/services/accounting-payroll"
  },
  {
    icon: Users,
    title: "Part-Time CFO Services",
    description: "Senior-level financial guidance without the full-time salary. Cash flow forecasting, budgeting, financial analysis, and strategic planning on a retainer basis — for growing businesses that need more than just a bookkeeper.",
    link: "/services/consulting"
  },
  {
    icon: Shield,
    title: "IRS Representation & Tax Resolution",
    description: "Facing an audit, lien, levy, or back taxes? Whether you're an individual or business, our team represents you directly before the IRS. We negotiate settlements, file delinquent returns, and protect your assets.",
    link: "/services/irs-relief"
  },
  {
    icon: Building,
    title: "Payroll & Business Setup",
    description: "Payroll processing, new business entity formation (LLC, S-Corp, C-Corp), EIN registration, state compliance, and the systems your business needs to operate properly from day one.",
    link: "/services/entity"
  }
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.08 } }
};

export const ServicesGrid = () => {
  return (
    <section
      id="services-section"
      className="relative bg-[#F7F6F2] py-20 lg:py-28"
      data-testid="welcome-services"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Everything you need. One firm.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4C5A70] max-w-2xl mx-auto">
            From daily bookkeeping to IRS audits, we handle the full spectrum of tax and accounting.
          </p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {services.map((svc) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={svc.title}
                variants={fadeUp}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md hover:border-[#D4AF37]/20 transition-all duration-300 group"
                data-testid={`service-card-${svc.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#0B2240] flex items-center justify-center mb-4 group-hover:bg-[#0B2240]/90 transition-colors">
                  <Icon className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-[#0B2240] text-base mb-2">
                  {svc.title}
                </h3>
                <p className="text-sm text-[#4C5A70] leading-relaxed">
                  {svc.description}
                </p>
                <Link
                  to={svc.link}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#D4AF37] hover:text-[#B99610] mt-3 transition-colors"
                  data-testid={`service-learn-more-${svc.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  Learn more
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
